import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useBurnup } from '../../../api/hooks';

// FEAT-015 Programme Burnup + Forecast. Two series — Scope (bar) and Completed (line) —
// per iteration, plus a deterministic Monte-Carlo forecast cone (P50 / P85 / P95 sprints
// to finish remaining work) shown as a stat strip below the chart.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  chartCard: {
    height: '24rem',
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold },
  statLabel: { color: tokens.colorNeutralForeground2 },
});

export function BurnupPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useBurnup(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Programme Burnup &amp; Forecast</Title2>
        <Body1 className={styles.hint}>
          Scope vs completed per iteration. The forecast is 10,000 Monte-Carlo sims against the last 6 sprints'
          velocity — finish sprints at the P50, P85, and P95 confidence levels.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see its burnup.</Body1>
      ) : isLoading ? (
        <Spinner label="Computing forecast…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.points.length === 0 ? (
        <Body1>Not enough iteration data yet to chart.</Body1>
      ) : (
        <>
          <div className={styles.chartCard}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.points}>
                <XAxis dataKey="iterationName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="scopePoints" fill={tokens.colorBrandBackground2} name="Scope (SP)" />
                <Line dataKey="completedPoints" stroke={tokens.colorBrandStroke1} strokeWidth={2} name="Completed (SP)" />
                <ReferenceLine y={0} stroke={tokens.colorNeutralStroke1} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <Title3>Forecast</Title3>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Remaining</Caption1>
              <span className={styles.statValue}>{data.remainingPoints.toFixed(0)} SP</span>
            </div>
            {data.forecast ? (
              <>
                <div className={styles.stat}>
                  <Caption1 className={styles.statLabel}>P50 finish</Caption1>
                  <span className={styles.statValue}>+{data.forecast.p50FinishSprint} sprints</span>
                </div>
                <div className={styles.stat}>
                  <Caption1 className={styles.statLabel}>P85 finish</Caption1>
                  <span className={styles.statValue}>+{data.forecast.p85FinishSprint} sprints</span>
                </div>
                <div className={styles.stat}>
                  <Caption1 className={styles.statLabel}>P95 finish</Caption1>
                  <span className={styles.statValue}>+{data.forecast.p95FinishSprint} sprints</span>
                </div>
                <div className={styles.stat}>
                  <Caption1 className={styles.statLabel}>Mean velocity</Caption1>
                  <span className={styles.statValue}>{data.forecast.meanVelocity.toFixed(1)} SP</span>
                </div>
              </>
            ) : (
              <div className={styles.stat}>
                <Caption1 className={styles.statLabel}>Forecast</Caption1>
                <Body1>Need at least 2 sprints of completed work.</Body1>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
